import axios from 'axios';
import { API_BASE_URL } from '../apiBase';
import { formatApiError } from '../context/AuthContext';
import { formatNotifyMessage } from './formatNotifyMessage';

/** Dígitos E.164 BR: 10/11 locais ganham 55; 12–13 já com país ficam iguais. */
export function normalizeWhatsAppDigits(phone) {
  let d = String(phone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.replace(/^00+/, '');
  if (d.startsWith('55') && d.length >= 12 && d.length <= 13) return d;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return d;
}

/**
 * Envia texto de acesso (teste/cliente) para o WhatsApp do cliente via Evolution.
 * Garante contato no CRM e usa /api/messages/send (aparece no Atendimentos).
 *
 * @returns {{ ok: true, contactId: string } | { ok: false, error: string, needsSetup?: boolean }}
 */
export async function sendAccessWhatsApp({
  phone,
  name,
  message,
  apiUrl = API_BASE_URL,
  notes = 'Criado via Dragon Tech (acesso automático)',
} = {}) {
  const content = String(message || '').trim();
  const digits = normalizeWhatsAppDigits(phone);
  if (!content) {
    return { ok: false, error: 'Mensagem vazia — nada para enviar.' };
  }
  if (digits.length < 12) {
    return { ok: false, error: 'WhatsApp inválido (informe DDD + número).' };
  }

  try {
    const { data: waCheck } = await axios.get(`${apiUrl}/api/whatsapp/needs-setup`, {
      withCredentials: true,
    });
    if (waCheck?.needs_setup) {
      try {
        window.dispatchEvent(new Event('aurax:wa-setup'));
      } catch {
        /* ignore */
      }
      return {
        ok: false,
        needsSetup: true,
        error: 'Configure e conecte o WhatsApp em Operação → WhatsApp antes de enviar.',
      };
    }
  } catch {
    /* se falhar a checagem, tenta enviar mesmo assim */
  }

  let contactId = null;
  try {
    const { data: contacts } = await axios.get(`${apiUrl}/api/contacts`, {
      params: { search: digits },
      withCredentials: true,
    });
    const list = Array.isArray(contacts) ? contacts : contacts?.items || contacts?.contacts || [];
    const match = list.find((c) => {
      const p = normalizeWhatsAppDigits(c.phone || c.whatsapp || '');
      return p === digits || p.endsWith(digits.slice(-11)) || digits.endsWith(p.slice(-11));
    });
    if (match?.id) contactId = match.id;
  } catch {
    /* cria abaixo */
  }

  if (!contactId) {
    try {
      const { data: created } = await axios.post(
        `${apiUrl}/api/contacts`,
        {
          name: String(name || digits).trim() || digits,
          phone: digits,
          notes,
        },
        { withCredentials: true },
      );
      contactId = created?.id || created?.contact?.id;
    } catch (e) {
      return {
        ok: false,
        error:
          formatNotifyMessage(formatApiError(e?.response?.data?.detail)) ||
          e?.message ||
          'Não foi possível criar o contato no CRM.',
      };
    }
  }

  if (!contactId) {
    return { ok: false, error: 'contato não encontrado e não foi possível criar.' };
  }

  try {
    await axios.post(
      `${apiUrl}/api/messages/send`,
      { contact_id: contactId, content },
      { withCredentials: true },
    );
    return { ok: true, contactId };
  } catch (e) {
    return {
      ok: false,
      error: formatNotifyMessage(
        formatApiError(e?.response?.data?.detail) ||
          e?.response?.data?.message ||
          e?.message ||
          'Falha ao enviar no WhatsApp.',
      ),
    };
  }
}
